// Reading a QR — from the live camera, or from a photo.
//
// NO NATIVE PLUGIN. The phone's webview is `http://localhost`, a secure context,
// so getUserMedia works there once the app holds the CAMERA permission
// (AndroidManifest + NSCameraUsageDescription — a Play / App Store build).
// Capacitor's web-chrome client asks the user for it on first use.
//
// ⚠️ UNTIL THAT BUILD, THE LIVE CAMERA FAILS — and the PHOTO path is what
// works: an <input type=file accept=image/*> opens the system camera or the
// gallery with no permission of the app's own, and the picture is decoded
// here. So scanning reaches every phone by OTA; the live view follows with the
// next store release.
//
// Decoding: the browser's BarcodeDetector where it exists (fast, native),
// jsQR everywhere else.

import jsQR from 'jsqr';

const MAX_SIDE = 1280;

let detector;
function nativeDetector() {
  if (detector !== undefined) return detector;
  try {
    // eslint-disable-next-line no-undef
    detector = typeof BarcodeDetector !== 'undefined' ? new BarcodeDetector({ formats: ['qr_code'] }) : null;
  } catch (e) {
    detector = null;
  }
  return detector;
}

function decodeCanvas(canvas, ctx) {
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const hit = jsQR(img.data, img.width, img.height, { inversionAttempts: 'attemptBoth' });
  return hit ? hit.data : null;
}

/** Draw `source` into `canvas`, scaled so its long side is at most MAX_SIDE. */
function paint(canvas, source, w, h) {
  const k = Math.min(1, MAX_SIDE / Math.max(w, h));
  canvas.width = Math.max(1, Math.round(w * k));
  canvas.height = Math.max(1, Math.round(h * k));
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return ctx;
}

/** A picked/taken photo -> the QR text, or null when none was found. */
export async function decodeImageFile(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('not an image'));
      i.src = url;
    });
    const det = nativeDetector();
    if (det) {
      const found = await det.detect(img).catch(() => []);
      if (found && found[0] && found[0].rawValue) return found[0].rawValue;
    }
    const canvas = document.createElement('canvas');
    const ctx = paint(canvas, img, img.naturalWidth || img.width, img.naturalHeight || img.height);
    return decodeCanvas(canvas, ctx);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Is a live camera even possible here? (No secure context / no API = no.) */
export const cameraAvailable = () => !!(typeof navigator !== 'undefined'
  && navigator.mediaDevices && navigator.mediaDevices.getUserMedia
  && (typeof window === 'undefined' || window.isSecureContext !== false));

/**
 * Start the back camera into `video` and call onCode(text) for each QR read
 * until it returns anything but false. -> stop() ; throws (NotAllowedError, NotFoundError…) if the camera
 * cannot be opened, so the caller can fall back to the photo path.
 */
export async function startCameraScan(video, onCode) {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: 'environment' } },
    audio: false,
  });
  let stopped = false;
  const stop = () => {
    stopped = true;
    stream.getTracks().forEach((t) => t.stop());
    if (video) video.srcObject = null;
  };

  video.srcObject = stream;
  video.setAttribute('playsinline', 'true');   // iOS: no full-screen takeover
  video.muted = true;
  await video.play().catch(() => {});

  const canvas = document.createElement('canvas');
  const det = nativeDetector();
  const tick = async () => {
    if (stopped) return;
    try {
      if (video.readyState >= 2 && video.videoWidth) {
        let text = null;
        if (det) {
          const found = await det.detect(video).catch(() => []);
          text = found && found[0] ? found[0].rawValue : null;
        } else {
          const ctx = paint(canvas, video, video.videoWidth, video.videoHeight);
          text = decodeCanvas(canvas, ctx);
        }
        // onCode returns false for "not the QR I want" — keep looking.
        if (text && !stopped && onCode(text) !== false) return;
      }
    } catch (e) { /* a bad frame — try the next */ }
    // ~5 reads a second: quick to catch, light on a cheap phone's CPU.
    setTimeout(tick, 200);
  };
  tick();
  return stop;
}
